import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { ChatTheme } from './types';
import { systemUpdatesApi, SystemUpdate } from '../../../domains/admin/api/systemUpdates';

interface ChatHeaderProps {
  onClose: () => void;
  onToggleTheme: () => void;
  onNewChat?: () => void;
  onMinimize?: () => void;
  isMinimized?: boolean;
  theme: ChatTheme;
  title?: string;
  subtitle?: string;
  headerGradient?: string;
  headerAccent?: string;
  logoBg?: string;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({
  onClose,
  onToggleTheme,
  onNewChat,
  onMinimize,
  isMinimized = false,
  theme,
  title = "EIS-TECH ASSISTANT",
  subtitle = "Yiontech Neural Engine",
  headerGradient,
  headerAccent = 'text-blue-400',
  logoBg = 'bg-blue-700'
}) => {
  const [showInfo, setShowInfo] = useState(false);
  const [showUpdates, setShowUpdates] = useState(false);
  const [activeTab, setActiveTab] = useState<'assistant' | 'updates'>('assistant');
  const [systemUpdates, setSystemUpdates] = useState<SystemUpdate[]>([]);
  const [updatesLoading, setUpdatesLoading] = useState(false);
  const infoRef = useRef<HTMLDivElement>(null);
  const infoBtnRef = useRef<HTMLButtonElement>(null);
  const updatesRef = useRef<HTMLDivElement>(null);
  const updatesBtnRef = useRef<HTMLButtonElement>(null);
  const [updatesDropdownPos, setUpdatesDropdownPos] = useState<React.CSSProperties>({});
  const [infoDropdownPos, setInfoDropdownPos] = useState<React.CSSProperties>({});

  // Load system updates when the tab opens
  const loadSystemUpdates = useCallback(async () => {
    setUpdatesLoading(true);
    try {
      const response = await systemUpdatesApi.getUnseenUpdates();
      setSystemUpdates(response.updates || []);
    } catch (err) {
      console.warn('Failed to load system updates:', err);
    } finally {
      setUpdatesLoading(false);
    }
  }, []);

  // Close popups when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (infoRef.current && !infoRef.current.contains(event.target as Node)) {
        setShowInfo(false);
      }
      if (showUpdates && updatesRef.current && !updatesRef.current.contains(event.target as Node) && updatesBtnRef.current && !updatesBtnRef.current.contains(event.target as Node)) {
        setShowUpdates(false);
      }
      if (showInfo && infoRef.current && !infoRef.current.contains(event.target as Node) && infoBtnRef.current && !infoBtnRef.current.contains(event.target as Node)) {
        setShowInfo(false);
      }
    };

    if (showInfo || showUpdates) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showInfo, showUpdates]);

  // Calculate dropdown position when shown
  useEffect(() => {
    if (showUpdates && updatesBtnRef.current) {
      const rect = updatesBtnRef.current.getBoundingClientRect();
      setUpdatesDropdownPos({
        top: rect.bottom + 8,
        left: Math.max(8, rect.left),
      });
    }
  }, [showUpdates]);

  useEffect(() => {
    if (showInfo && infoBtnRef.current) {
      const rect = infoBtnRef.current.getBoundingClientRect();
      setInfoDropdownPos({
        top: rect.bottom + 8,
        right: Math.max(8, window.innerWidth - rect.right),
      });
    }
  }, [showInfo]);

  const handleShowInfo = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowInfo(!showInfo);
  };

  const handleTabClick = (tab: 'assistant' | 'updates') => {
    setActiveTab(tab);
    if (tab === 'updates') {
      setShowUpdates(true);
      loadSystemUpdates();
    } else {
      setShowUpdates(false);
    }
  };

  const getCategoryColor = (category: string): string => {
    switch (category) {
      case 'security': return 'text-red-600 bg-red-50';
      case 'feature': return 'text-blue-600 bg-blue-50';
      case 'maintenance': return 'text-amber-600 bg-amber-50';
      case 'compliance': return 'text-purple-600 bg-purple-50';
      default: return 'text-slate-600 bg-slate-50';
    }
  };

  // ── Compact minimized header ──────────────────────────────────────────
  if (isMinimized) {
    return (
      <header className={`h-12 ${headerGradient || 'bg-slate-800/90'} backdrop-blur-xl border-b border-white/10 flex items-center justify-between px-3 shrink-0`}>
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={`w-7 h-7 ${logoBg} rounded-lg flex items-center justify-center shadow`}>
            <svg width="14" height="14" viewBox="0 0 512 512" fill="none">
              <defs>
                <linearGradient id="logo-bg-m" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#ffffff"/><stop offset="100%" stopColor="#f1f5f9"/></linearGradient>
              </defs>
              <circle cx="256" cy="180" r="32" fill="url(#logo-bg-m)"/>
              <path d="M170 260 Q256 340 256 320 Q256 340 342 260" fill="url(#logo-bg-m)"/>
            </svg>
          </div>
          <div className="min-w-0">
            <h1 className="text-xs font-bold text-white leading-tight truncate">{title}</h1>
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
              <p className="text-[9px] text-emerald-400 font-semibold">Online</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {/* Expand button */}
          {onMinimize && (
            <button
              onClick={onMinimize}
              className="p-1.5 text-slate-400 rounded-lg hover:bg-slate-700 hover:text-white transition-all"
              aria-label="Expand chat"
              title="Expand"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </button>
          )}
          {/* New chat */}
          <button
            onClick={onNewChat}
            className="p-1.5 text-slate-400 rounded-lg hover:bg-blue-600/80 hover:text-white transition-all"
            aria-label="New chat"
            title="New conversation"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
          {/* Close */}
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 rounded-lg hover:bg-red-600/80 hover:text-white transition-all"
            aria-label="Close assistant"
            title="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </header>
    );
  }

  // ── Full expanded header ────────────────────────────────────────────
  return (
    <header className={`h-16 xl:h-20 ${headerGradient || 'bg-slate-800/90'} backdrop-blur-xl border-b border-white/10 flex items-center justify-between px-4 sm:px-6 xl:px-10 shrink-0`}>
      {/* Left: Brand + Tabs */}
      <div className="flex items-center gap-3 sm:gap-5 xl:gap-8 min-w-0">
        {/* Brand */}
        <div className="flex items-center gap-3 xl:gap-4 shrink-0">
          <div className={`w-10 h-10 xl:w-12 xl:h-12 ${logoBg} rounded-xl flex items-center justify-center shadow-lg`}>
            <svg width="20" height="20" viewBox="0 0 512 512" fill="none">
              <defs>
                <linearGradient id="logo-bg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#ffffff"/><stop offset="100%" stopColor="#f1f5f9"/></linearGradient>
                <linearGradient id="logo-lf" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#60a5fa"/><stop offset="100%" stopColor="#3b82f6"/></linearGradient>
              </defs>
              <path d="M256 56 C390 56 456 150 456 256" fill="none" stroke="url(#logo-bg)" strokeWidth="24" strokeLinecap="round"/>
              <path d="M56 256 C56 150 122 56 256 56" fill="none" stroke="url(#logo-bg)" strokeWidth="24" strokeLinecap="round"/>
              <circle cx="256" cy="180" r="32" fill="url(#logo-bg)"/>
              <path d="M170 260 Q256 340 256 320 Q256 340 342 260" fill="url(#logo-bg)"/>
              <path d="M256 340 Q140 340 100 420 Q180 380 256 456 Z" fill="url(#logo-lf)"/>
              <path d="M256 340 Q372 340 412 420 Q332 380 256 456 Z" fill="url(#logo-lf)"/>
            </svg>
          </div>
          <div className="hidden sm:block ai-header-title">
            <h1 className="text-sm xl:text-base font-bold text-white leading-tight">{title}</h1>
            <p className={`text-xs ${headerAccent} font-semibold uppercase tracking-wider mt-0.5`}>{subtitle}</p>
          </div>
        </div>

        {/* Separator */}
        <div className="hidden lg:block ai-header-divider h-8 w-px bg-slate-100" />

        {/* Nav Tabs */}
        <nav className="hidden md:flex ai-tab-nav gap-1 bg-slate-700/50 p-1 rounded-xl relative">
          <button
            onClick={() => handleTabClick('assistant')}
            className={`px-3 xl:px-4 py-1.5 xl:py-2 text-xs xl:text-sm font-semibold rounded-lg transition-all ${
              activeTab === 'assistant'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-400 hover:text-white hover:bg-slate-600/50'
            }`}
          >
            Assistant
          </button>
          <button
            ref={updatesBtnRef}
            onClick={() => handleTabClick('updates')}
            className={`px-3 xl:px-4 py-1.5 xl:py-2 text-xs xl:text-sm font-semibold rounded-lg transition-all ${
              activeTab === 'updates'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-400 hover:text-white hover:bg-slate-600/50'
            }`}
          >
            System Updates
          </button>

          {/* System Updates Dropdown — portalled to body to avoid overflow clipping */}
          {showUpdates && createPortal(
            <div
              ref={updatesRef}
              style={updatesDropdownPos}
              className={`fixed w-96 max-h-[28rem] ${theme.isDark ? 'bg-slate-800 border border-slate-600 text-white' : 'bg-white border border-slate-200 text-slate-900'} text-sm rounded-2xl shadow-xl z-[9999] overflow-hidden`}
            >
              <div className={`p-3 border-b ${theme.isDark ? 'border-slate-600' : 'border-slate-100'} flex items-center justify-between`}>
                <div className={`font-bold ${theme.isDark ? 'text-white' : 'text-slate-900'} text-xs uppercase tracking-widest`}>System Update History</div>
                <span className={`text-[10px] ${theme.isDark ? 'text-slate-500' : 'text-slate-400'} font-medium`}>{systemUpdates.length} updates</span>
              </div>
              <div className="max-h-80 overflow-y-auto hover-scroll">
                {updatesLoading ? (
                  <div className="p-6">
                    <div className={`w-5 h-5 border-2 ${theme.isDark ? 'border-slate-600 border-t-blue-400' : 'border-slate-200 border-t-blue-500'} rounded-full animate-spin mx-auto mb-2`} />
                    <p className={`text-xs ${theme.isDark ? 'text-slate-500' : 'text-slate-400'}`}>Loading updates...</p>
                  </div>
                ) : systemUpdates.length === 0 ? (
                  <div className={`p-6 ${theme.isDark ? 'text-slate-500' : 'text-slate-400'} text-center text-xs`}>
                    No system updates available
                  </div>
                ) : (
                  systemUpdates.map((update) => (
                    <div
                      key={update.id}
                      className="p-3 border-b border-slate-50 last:border-b-0 hover:bg-slate-50 transition-all"
                    >
                      <div className="flex items-start gap-2">
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${getCategoryColor(update.category)}`}>
                          {update.category}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className={`font-semibold ${theme.isDark ? 'text-slate-200' : 'text-slate-800'} text-xs text-left`}>{update.title}</div>
                          <div className={`text-[11px] ${theme.isDark ? 'text-slate-400' : 'text-slate-500'} mt-0.5 text-left leading-relaxed`}>{update.summary}</div>
                          <div className={`text-[10px] ${theme.isDark ? 'text-slate-500' : 'text-slate-400'} mt-1`}>
                            {new Date(update.created_at).toLocaleDateString()} · v{update.version}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>,
            document.body
          )}
        </nav>
      </div>

      {/* Right: Status + Actions */}
      <div className="flex items-center gap-2 xl:gap-4 shrink-0">
        {/* System status */}
        <div className="hidden lg:block ai-header-status text-right">
          <p className={`text-[9px] xl:text-[10px] font-bold ${theme.isDark ? 'text-slate-500' : 'text-slate-400'} uppercase`}>Status</p>
          <div className="flex items-center gap-1 justify-end">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
            <p className="text-[10px] xl:text-xs font-bold text-emerald-500">Online</p>
          </div>
        </div>

        {/* Mobile updates toggle */}
        <button
          onClick={() => {
            setShowUpdates(!showUpdates);
            if (!showUpdates) loadSystemUpdates();
          }}
          className="md:hidden p-2.5 text-slate-400 rounded-xl bg-slate-800/50 hover:bg-slate-700 hover:text-slate-200 transition-all duration-200 backdrop-blur-sm border border-slate-600/30 hover:border-slate-500/50"
          aria-label="System updates"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </button>

        {/* New chat */}
        <button
          onClick={onNewChat}
          className="p-2.5 text-slate-400 rounded-xl bg-slate-800/50 hover:bg-blue-600/80 hover:text-white transition-all duration-200 backdrop-blur-sm border border-slate-600/30 hover:border-blue-500/50 shadow-lg hover:shadow-blue-500/25"
          aria-label="New chat"
          title="New conversation"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>

        {/* Theme toggle */}
        <button
          onClick={onToggleTheme}
          className="p-2.5 text-slate-400 rounded-xl bg-slate-800/50 hover:bg-slate-700 hover:text-slate-200 transition-all duration-200 backdrop-blur-sm border border-slate-600/30 hover:border-slate-500/50"
          aria-label="Toggle theme"
          title="Toggle theme"
        >
          {theme.isDark ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </button>

        {/* YionTech Info */}
        <button
          ref={infoBtnRef}
          onClick={handleShowInfo}
          data-info-button
          className="p-2.5 text-slate-400 rounded-xl bg-slate-800/50 hover:bg-slate-700 hover:text-slate-200 transition-all duration-200 backdrop-blur-sm border border-slate-600/30 hover:border-slate-500/50"
          aria-label="YionTech Information"
          title="About YionTech"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>

        {showInfo && createPortal(
          <div
            ref={infoRef}
            style={infoDropdownPos}
            className={`fixed p-5 ${theme.isDark ? 'bg-slate-800 border border-slate-600 text-white' : 'bg-white border border-slate-200 text-slate-900'} text-sm rounded-2xl shadow-xl w-64 z-[9999]`}
          >
            <div className="text-center">
              <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center text-white font-bold text-xs mx-auto mb-2">EIS</div>
              <div className={`${theme.isDark ? 'text-slate-400' : 'text-slate-600'} text-[11px] leading-relaxed mb-2`}>
                YionTech Advanced AI solutions for enterprise compliance and intelligent automation.
              </div>
              <a href="https://yiontech.co.uk" target="_blank" rel="noopener noreferrer" className={`text-blue-600 text-[11px] font-medium ${theme.isDark ? 'hover:text-blue-400' : 'hover:text-blue-800'} transition-colors`}>
                yiontech.co.uk
              </a>
            </div>
          </div>,
          document.body
        )}

        {/* Minimize button */}
        {onMinimize && (
          <button
            onClick={onMinimize}
            className="p-2.5 text-slate-400 rounded-xl bg-slate-800/50 hover:bg-slate-700 hover:text-slate-200 transition-all duration-200 backdrop-blur-sm border border-slate-600/30 hover:border-slate-500/50"
            aria-label="Minimize chat"
            title="Minimize"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
        )}

        {/* Close button */}
        <button
          onClick={onClose}
          className="w-10 h-10 xl:w-12 xl:h-12 bg-red-600/80 hover:bg-red-600 text-white rounded-xl flex items-center justify-center transition-all duration-200 backdrop-blur-sm border border-red-500/30 hover:border-red-400/50 shadow-lg hover:shadow-red-500/25"
          aria-label="Close assistant"
          title="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 xl:w-6 xl:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </header>
  );
};

export default ChatHeader;
